import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import { Box, Chip, Grid, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCopilotDrawerState } from 'components/Copilot';
import Loader, { LoadingStage } from 'components/Loader';
import PageHeader from 'components/PageHeader';
import SiteHeader, { SITE_HEADER_HEIGHT } from 'components/SiteHeader/SiteHeader';
import { find, map, some } from 'lodash';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentLabId } from 'utils/useCurrentLab';

import { getNucleaiOriginsCloseLocation, NucleaiOriginsRouteState, OriginsTabValue } from './navigation';

interface OriginsTabDefinition {
  value: OriginsTabValue;
  label: string;
  icon: React.ReactElement;
  kicker: string;
}

interface OriginsTabModule {
  default: React.ComponentType;
}

type IdleCallbackHandle = number;

const loadModelsTab = () => import('./ModelsTab');
const loadModelTrainingSessionsTab = () => import('./ModelTrainingSessionsTab');
const loadDatasetsTab = () => import('./DatasetsTab');
const loadExperimentalReportsTab = () => import('./ExperimentalReportsTab');
const loadVectorRepositoriesTab = () => import('./VectorRepositoriesTab');

const ModelsTab = React.lazy(loadModelsTab);
const ModelTrainingSessionsTab = React.lazy(loadModelTrainingSessionsTab);
const DatasetsTab = React.lazy(loadDatasetsTab);
const ExperimentalReportsTab = React.lazy(loadExperimentalReportsTab);
const VectorRepositoriesTab = React.lazy(loadVectorRepositoriesTab);

const idlePreloadDelayMs = 250;

const originsTabs: OriginsTabDefinition[] = [
  {
    value: 'models',
    label: 'Models',
    icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
    kicker: 'Catalog and govern reusable model assets',
  },
  {
    value: 'model-training-sessions',
    label: 'Model Training Sessions',
    icon: <LayersOutlinedIcon fontSize="small" />,
    kicker: 'Track cross-lab training runs, execution status, and dataset scope',
  },
  {
    value: 'datasets',
    label: 'Datasets',
    icon: <DatasetOutlinedIcon fontSize="small" />,
    kicker: 'Manage shared cohorts, lineage, and report-ready slices',
  },
  {
    value: 'experimental-reports',
    label: 'Experimental Reports',
    icon: <AutoGraphOutlinedIcon fontSize="small" />,
    kicker: 'Assemble text-first sections with Plotly-backed visual evidence',
  },
  {
    value: 'vector-repositories',
    label: 'Vector Repositories',
    icon: <HubOutlinedIcon fontSize="small" />,
    kicker: 'Slide tile embedding repositories with explicit model and vector contracts',
  },
];

const originsTabLoaders: Record<OriginsTabValue, () => Promise<OriginsTabModule>> = {
  models: loadModelsTab,
  'model-training-sessions': loadModelTrainingSessionsTab,
  datasets: loadDatasetsTab,
  'experimental-reports': loadExperimentalReportsTab,
  'vector-repositories': loadVectorRepositoriesTab,
};

const originsTabComponents: Record<OriginsTabValue, React.LazyExoticComponent<React.ComponentType>> = {
  models: ModelsTab,
  'model-training-sessions': ModelTrainingSessionsTab,
  datasets: DatasetsTab,
  'experimental-reports': ExperimentalReportsTab,
  'vector-repositories': VectorRepositoriesTab,
};

const isOriginsTabValue = (value: string | undefined | null): value is OriginsTabValue => some(originsTabs, { value });

const scheduleOriginsIdleWork = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  if ('requestIdleCallback' in window) {
    const idleHandle = window.requestIdleCallback(() => callback()) as IdleCallbackHandle;

    return () => {
      window.cancelIdleCallback(idleHandle);
    };
  }

  const timeoutHandle = setTimeout(callback, idlePreloadDelayMs);

  return () => {
    clearTimeout(timeoutHandle);
  };
};

const buildOriginsSearchWithTab = (search: string, tab: OriginsTabValue) => {
  const searchParams = new URLSearchParams(search);

  searchParams.set('tab', tab);

  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : '';
};

const NucleaiOriginsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { labId } = useCurrentLabId();
  const { copilotDrawerOpen, copilotDrawerWidth } = useCopilotDrawerState();
  const routeState = (location.state as NucleaiOriginsRouteState | null) || null;
  const requestedTab = React.useMemo(() => new URLSearchParams(location.search).get('tab'), [location.search]);
  const activeTab: OriginsTabValue = isOriginsTabValue(requestedTab) ? requestedTab : 'models';
  const selectedTab = React.useMemo(() => find(originsTabs, { value: activeTab }) || originsTabs[0], [activeTab]);
  const ActiveTabComponent = originsTabComponents[activeTab];
  const preloadedTabValuesRef = React.useRef<Set<OriginsTabValue>>(new Set([activeTab]));
  const copilotDrawerLayoutTransitionDuration = `var(--copilot-drawer-layout-transition-duration, ${theme.transitions.duration.enteringScreen}ms)`;

  const locationRef = React.useRef(location);
  locationRef.current = location;

  const setOriginsTab = React.useCallback(
    (nextTab: OriginsTabValue) => {
      const currentLocation = locationRef.current;

      navigate(
        {
          pathname: currentLocation.pathname,
          search: buildOriginsSearchWithTab(currentLocation.search, nextTab),
        },
        {
          replace: true,
          state: currentLocation.state,
        }
      );
    },
    [navigate]
  );

  React.useEffect(() => {
    if (requestedTab !== activeTab) {
      setOriginsTab(activeTab);
    }
  }, [activeTab, requestedTab, setOriginsTab]);

  React.useEffect(() => {
    preloadedTabValuesRef.current.add(activeTab);

    const unloadedTabValues = map(originsTabs, 'value').filter(
      (tabValue) => !preloadedTabValuesRef.current.has(tabValue)
    );

    if (!unloadedTabValues.length) {
      return undefined;
    }

    return scheduleOriginsIdleWork(() => {
      unloadedTabValues.forEach((tabValue) => {
        preloadedTabValuesRef.current.add(tabValue);
        void originsTabLoaders[tabValue]();
      });
    });
  }, [activeTab]);

  const handleClose = React.useCallback(() => {
    navigate(
      getNucleaiOriginsCloseLocation({
        state: routeState,
        labId: routeState?.originsContext?.labId || labId,
      })
    );
  }, [labId, navigate, routeState]);

  return (
    <>
      <SiteHeader />
      <Box
        sx={{
          position: 'fixed',
          top: SITE_HEADER_HEIGHT,
          left: 0,
          right: copilotDrawerOpen ? `${copilotDrawerWidth}px` : 0,
          bottom: 0,
          px: { xs: 0, sm: 2 },
          py: { xs: 0, sm: 2 },
          backgroundColor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.common.black, 0.08)
              : alpha(theme.palette.common.black, 0.2),
          backdropFilter: 'blur(8px)',
          transition: `right ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
          zIndex: theme.zIndex.appBar - 1,
        }}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Nucleai Origins"
          sx={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Grid
            container
            direction="column"
            px={{ xs: 3, md: 6 }}
            pt={4}
            pb={4}
            spacing={3}
            sx={{ width: '100%', minWidth: 0, flexWrap: 'nowrap' }}
          >
            <Grid item>
              <PageHeader
                pageTitle="Nucleai Origins"
                trailingAction={
                  <IconButton onClick={handleClose} size="small" aria-label="Close Nucleai Origins">
                    <CloseRoundedIcon />
                  </IconButton>
                }
                pageSubtitle={
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Cross-lab workspace for model stewardship, model training sessions, shared datasets, vector
                      repositories, and experimental reporting.
                    </Typography>
                    <Chip label="Nucleai only" size="small" variant="outlined" />
                    <Chip label="Cross-lab" size="small" variant="outlined" />
                  </Stack>
                }
              />
            </Grid>

            <Grid item>
              <Tabs
                value={activeTab}
                onChange={(_event, nextValue: OriginsTabValue) => setOriginsTab(nextValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Nucleai Origins sections"
              >
                {map(originsTabs, (tabDefinition) => (
                  <Tab
                    key={tabDefinition.value}
                    id={`nucleai-origins-tab-${tabDefinition.value}`}
                    value={tabDefinition.value}
                    icon={tabDefinition.icon}
                    iconPosition="start"
                    label={tabDefinition.label}
                    sx={{ textTransform: 'none', alignItems: 'flex-start' }}
                  />
                ))}
              </Tabs>
            </Grid>

            <Grid item>
              <Typography variant="body2" color="text.secondary">
                {selectedTab.kicker}
              </Typography>
            </Grid>

            <Grid item>
              <Box sx={{ width: '100%' }} role="tabpanel" aria-labelledby={`nucleai-origins-tab-${activeTab}`}>
                <React.Suspense fallback={<Loader stage={LoadingStage.LoadingDashboard} />}>
                  <ActiveTabComponent />
                </React.Suspense>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default NucleaiOriginsPage;
